import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, update, remove, get } from '../../Config/matuRtdb';
import { 
  Users, 
  Crown, 
  UserCheck, 
  UserX, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye,
  Ban,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  Calendar,
  Shield,
  Star,
  MoreVertical,
  Download,
  RefreshCw
} from 'lucide-react';

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('todos');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    vip: 0,
    free: 0,
    banned: 0,
    active: 0
  });

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, filter]);

  const loadUsers = () => {
    const db = getDatabase();
    const usersRef = ref(db, 'usuarios');
    
    const unsubscribe = onValue(usersRef, (snapshot) => {
      if (snapshot.exists()) {
        const usersData = snapshot.val();
        const usersArray = Object.entries(usersData).map(([uid, user]) => ({
          uid,
          ...user,
          createdAt: user.createdAt || new Date().toISOString(),
          lastLogin: user.lastLogin || null,
          isActive: user.isActive !== false,
          isBanned: user.isBanned === true
        }));
        
        setUsers(usersArray);
        calculateStats(usersArray);
      } else {
        setUsers([]);
        setStats({ total: 0, vip: 0, free: 0, banned: 0, active: 0 });
      }
      setLoading(false);
    });

    return () => unsubscribe();
  };

  const calculateStats = (usersArray) => {
    const stats = {
      total: usersArray.length,
      vip: usersArray.filter(user => user.tipoCuenta === 'VIP' || user.isVip === true || user.vip === true).length,
      free: usersArray.filter(user => !(user.tipoCuenta === 'VIP' || user.isVip === true || user.vip === true)).length,
      banned: usersArray.filter(user => user.isBanned === true).length,
      active: usersArray.filter(user => user.isActive !== false).length
    };
    setStats(stats);
  };

  const filterUsers = () => {
    let filtered = users;

    // Filtrar por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.includes(searchTerm)
      );
    }

    // Filtrar por tipo
    switch (filter) {
      case 'vip':
        filtered = filtered.filter(user => 
          user.tipoCuenta === 'VIP' || user.isVip === true || user.vip === true
        );
        break;
      case 'free':
        filtered = filtered.filter(user => 
          !(user.tipoCuenta === 'VIP' || user.isVip === true || user.vip === true)
        );
        break;
      case 'banned':
        filtered = filtered.filter(user => user.isBanned === true);
        break;
      case 'active':
        filtered = filtered.filter(user => user.isActive !== false);
        break;
      default:
        break;
    }

    setFilteredUsers(filtered);
  };

  const handleUserAction = async (uid, action, value) => {
    const db = getDatabase();
    const userRef = ref(db, `usuarios/${uid}`);
    
    try {
      switch (action) {
        case 'toggleVip':
          await update(userRef, { 
            tipoCuenta: value ? 'VIP' : 'Gratis',
            isVip: value,
            vip: value,
            updatedAt: new Date().toISOString()
          });
          break;
        case 'toggleBan':
          await update(userRef, { 
            isBanned: value,
            updatedAt: new Date().toISOString()
          });
          break;
        case 'delete':
          if (window.confirm('¿Estás seguro de que quieres eliminar este usuario? Esta acción no se puede deshacer.')) {
            await remove(userRef);
          }
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error actualizando usuario:', error);
      alert('Error al actualizar el usuario');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async (updatedData) => {
    const db = getDatabase();
    const userRef = ref(db, `usuarios/${editingUser.uid}`);
    
    try {
      await update(userRef, {
        ...updatedData,
        updatedAt: new Date().toISOString()
      });
      setIsEditModalOpen(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error guardando cambios:', error);
      alert('Error al guardar los cambios');
    }
  };

  const viewUserDetails = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const getAccountTypeColor = (user) => {
    if (user.tipoCuenta === 'VIP' || user.isVip === true || user.vip === true) {
      return 'text-yellow-400';
    }
    return 'text-blue-400';
  };

  const getAccountTypeIcon = (user) => {
    if (user.tipoCuenta === 'VIP' || user.isVip === true || user.vip === true) {
      return Crown;
    }
    return UserCheck;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestión de Usuarios</h2>
          <p className="text-white/60">Administra usuarios y permisos</p>
        </div>
        <button
          onClick={loadUsers}
          className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <RefreshCw size={16} />
          <span>Actualizar</span>
        </button>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Total</p>
              <p className="text-xl font-bold text-white">{stats.total}</p>
            </div>
            <Users className="text-blue-400 w-5 h-5" />
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">VIP</p>
              <p className="text-xl font-bold text-yellow-400">{stats.vip}</p>
            </div>
            <Crown className="text-yellow-400 w-5 h-5" />
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Gratis</p>
              <p className="text-xl font-bold text-blue-400">{stats.free}</p>
            </div>
            <UserCheck className="text-blue-400 w-5 h-5" />
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Activos</p>
              <p className="text-xl font-bold text-green-400">{stats.active}</p>
            </div>
            <CheckCircle className="text-green-400 w-5 h-5" />
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Baneados</p>
              <p className="text-xl font-bold text-red-400">{stats.banned}</p>
            </div>
            <UserX className="text-red-400 w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Filtros y búsqueda */}
      <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60 w-4 h-4" />
            <input
              type="text"
              placeholder="Buscar por nombre, email o teléfono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex space-x-2">
            {['todos', 'vip', 'free', 'banned', 'active'].map((filterType) => (
              <button
                key={filterType}
                onClick={() => setFilter(filterType)}
                className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                  filter === filterType
                    ? 'bg-blue-600 text-white'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Lista de usuarios */}
      <div className="bg-white/10 backdrop-blur-lg rounded-lg border border-white/20 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="px-4 py-3 text-left text-white font-medium">Usuario</th>
                <th className="px-4 py-3 text-left text-white font-medium">Tipo</th>
                <th className="px-4 py-3 text-left text-white font-medium">Estado</th>
                <th className="px-4 py-3 text-left text-white font-medium">Registro</th>
                <th className="px-4 py-3 text-left text-white font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-white/60">
                    No se encontraron usuarios
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const AccountIcon = getAccountTypeIcon(user);
                  return (
                    <tr key={user.uid} className="hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                            </span>
                          </div>
                          <div>
                            <p className="text-white font-medium">{user.name || 'Sin nombre'}</p>
                            <p className="text-white/60 text-sm">{user.email || 'Sin email'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <AccountIcon className={`w-4 h-4 ${getAccountTypeColor(user)}`} />
                          <span className={`text-sm font-medium ${getAccountTypeColor(user)}`}>
                            {user.tipoCuenta === 'VIP' || user.isVip === true || user.vip === true ? 'VIP' : 'Gratis'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          {user.isBanned ? (
                            <>
                              <UserX className="w-4 h-4 text-red-400" />
                              <span className="text-red-400 text-sm font-medium">Baneado</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 text-green-400" />
                              <span className="text-green-400 text-sm font-medium">Activo</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-white/60 text-sm">{formatDate(user.createdAt)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => viewUserDetails(user)}
                            className="p-2 text-blue-400 hover:bg-blue-400/20 rounded-lg transition-colors"
                            title="Ver detalles"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleEditUser(user)}
                            className="p-2 text-yellow-400 hover:bg-yellow-400/20 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleUserAction(user.uid, 'toggleVip', !(user.tipoCuenta === 'VIP' || user.isVip === true || user.vip === true))}
                            className={`p-2 rounded-lg transition-colors ${
                              user.tipoCuenta === 'VIP' || user.isVip === true || user.vip === true
                                ? 'text-red-400 hover:bg-red-400/20'
                                : 'text-green-400 hover:bg-green-400/20'
                            }`}
                            title={user.tipoCuenta === 'VIP' || user.isVip === true || user.vip === true ? 'Quitar VIP' : 'Hacer VIP'}
                          >
                            <Crown size={16} />
                          </button>
                          <button
                            onClick={() => handleUserAction(user.uid, 'toggleBan', !user.isBanned)}
                            className={`p-2 rounded-lg transition-colors ${
                              user.isBanned
                                ? 'text-green-400 hover:bg-green-400/20'
                                : 'text-red-400 hover:bg-red-400/20'
                            }`}
                            title={user.isBanned ? 'Desbanear' : 'Banear'}
                          >
                            <Ban size={16} />
                          </button>
                          <button
                            onClick={() => handleUserAction(user.uid, 'delete')}
                            className="p-2 text-red-400 hover:bg-red-400/20 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de detalles del usuario */}
      {isModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Detalles del Usuario</h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-xl">
                      {selectedUser.name ? selectedUser.name.charAt(0).toUpperCase() : 'U'}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{selectedUser.name || 'Sin nombre'}</h4>
                    <p className="text-gray-600">{selectedUser.email || 'Sin email'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">{selectedUser.email || 'No disponible'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">{selectedUser.phone || 'No disponible'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">Registrado: {formatDate(selectedUser.createdAt)}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">
                      Tipo: {selectedUser.tipoCuenta === 'VIP' || selectedUser.isVip === true || selectedUser.vip === true ? 'VIP' : 'Gratis'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Star className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">
                      Estado: {selectedUser.isBanned ? 'Baneado' : 'Activo'}
                    </span>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      handleEditUser(selectedUser);
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
                  >
                    Editar Usuario
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edición */}
      {isEditModalOpen && editingUser && (
        <EditUserModal
          user={editingUser}
          onSave={handleSaveEdit}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingUser(null);
          }}
        />
      )}
    </div>
  );
};

// Componente modal de edición
const EditUserModal = ({ user, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: user.name || '',
    email: user.email || '',
    phone: user.phone || '',
    tipoCuenta: user.tipoCuenta === 'VIP' || user.isVip === true || user.vip === true ? 'VIP' : 'Gratis',
    isBanned: user.isBanned || false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">Editar Usuario</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XCircle size={20} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Cuenta</label>
              <select
                value={formData.tipoCuenta}
                onChange={(e) => setFormData({...formData, tipoCuenta: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Gratis">Gratis</option>
                <option value="VIP">VIP</option>
              </select>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isBanned"
                checked={formData.isBanned}
                onChange={(e) => setFormData({...formData, isBanned: e.target.checked})}
                className="mr-2"
              />
              <label htmlFor="isBanned" className="text-sm text-gray-700">Usuario baneado</label>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Guardar Cambios
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserManager; 